import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect } from "react";
import { Modal } from "@project-mgmt/ui";
import { Button } from "@project-mgmt/ui";
import { FormField, Input } from "@project-mgmt/ui";
import { VentureFundingProgress } from "./VentureFundingProgress";
import { VentureCategoryBadge } from "./VentureCategoryBadge";
import { MIN_PLEDGE_AMOUNT } from "../model/constants";
/**
 * Modal component for creating a pledge to a venture
 *
 * Requirements:
 * - 6.1: Pledge authentication (handled by parent)
 * - 6.2: Pledge amount validation (minimum amount)
 * - 6.3: Pledge creation
 * - 6.4: Pledge confirmation step
 *
 * Features:
 * - Two-step process: amount input → confirmation
 * - Venture summary display
 * - Current funding status
 * - Amount validation with minimum check
 * - Loading and error states
 * - Mobile-friendly with large touch targets
 * - Accessible with ARIA labels
 */
export const PledgeModal = ({ isOpen, onClose, venture, onPledge, isLoading = false }) => {
    // State management
    const [step, setStep] = useState("input");
    const [amount, setAmount] = useState("");
    const [error, setError] = useState("");
    // Reset state when modal opens/closes
    useEffect(() => {
        if (isOpen) {
            setStep("input");
            setAmount("");
            setError("");
        }
    }, [isOpen]);
    // Format currency for display
    const formatCurrency = (value) => {
        return new Intl.NumberFormat("en-US", {
            style: "currency",
            currency: "USD",
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        }).format(value);
    };
    // Validate amount input
    const validateAmount = (value) => {
        setError("");
        if (!value || value.trim() === "") {
            setError("Please enter an amount");
            return false;
        }
        const numValue = parseFloat(value);
        if (isNaN(numValue)) {
            setError("Please enter a valid number");
            return false;
        }
        if (numValue < MIN_PLEDGE_AMOUNT) {
            setError(`Minimum pledge amount is ${formatCurrency(MIN_PLEDGE_AMOUNT)}`);
            return false;
        }
        if (numValue > 1000000) {
            setError("Maximum pledge amount is $1,000,000");
            return false;
        }
        return true;
    };
    // Handle amount input change
    const handleAmountChange = (e) => {
        const value = e.target.value;
        // Allow only numbers and decimal point
        if (value === "" || /^\d*\.?\d{0,2}$/.test(value)) {
            setAmount(value);
            setError("");
        }
    };
    // Handle continue to confirmation
    const handleContinue = () => {
        if (validateAmount(amount)) {
            setStep("confirm");
        }
    };
    // Handle back to input
    const handleBack = () => {
        setStep("input");
        setError("");
    };
    // Handle pledge submission
    const handleSubmit = async () => {
        const numAmount = parseFloat(amount);
        if (!validateAmount(amount)) {
            return;
        }
        const result = await onPledge(numAmount);
        if (result.success) {
            onClose();
        }
        else if (result.error) {
            setError(result.error);
            setStep("input");
        }
    };
    // Handle Enter key press
    const handleKeyPress = (e) => {
        if (e.key === "Enter" && !isLoading) {
            if (step === "input") {
                handleContinue();
            }
            else {
                handleSubmit();
            }
        }
    };
    return (_jsx(Modal, { isOpen: isOpen, onClose: onClose, title: "Pledge to Venture", size: "md", className: "max-h-[90vh] overflow-y-auto", children: _jsxs("div", { className: "space-y-6", children: [_jsxs("div", { className: "space-y-3", children: [_jsxs("div", { className: "flex items-start justify-between gap-3", children: [_jsxs("div", { className: "min-w-0 flex-1", children: [_jsx("h3", { className: "truncate text-lg font-semibold text-black", children: venture.name }), _jsxs("p", { className: "mt-1 text-sm text-gray-600", children: ["by ", venture.creator.name || "Anonymous"] })] }), _jsx(VentureCategoryBadge, { category: venture.type, size: "sm" })] }), _jsxs("div", { className: "space-y-3 rounded-lg bg-gray-50 p-4", children: [_jsxs("div", { className: "flex items-center justify-between text-sm", children: [_jsx("span", { className: "text-gray-600", children: "Current Progress" }), _jsxs("span", { className: "font-semibold text-black", children: [(venture.fundingProgress ?? 0).toFixed(0), "%"] })] }), _jsx(VentureFundingProgress, { fundsPledged: venture.fundsPledged, targetGoal: venture.targetGoal, size: "md", showAmounts: true })] })] }), step === "input" && (_jsxs("div", { className: "space-y-4", children: [_jsx(FormField, { label: "Pledge Amount", error: error, required: true, children: _jsxs("div", { className: "relative", children: [_jsx("span", { className: "absolute left-3 top-1/2 -translate-y-1/2 text-lg text-gray-500", children: "$" }), _jsx(Input, { type: "text", inputMode: "decimal", placeholder: "0.00", value: amount, onChange: handleAmountChange, onKeyPress: handleKeyPress, error: !!error, disabled: isLoading, className: "pl-8 text-lg font-semibold", autoFocus: true, "aria-label": "Pledge amount in dollars" })] }) }), _jsxs("p", { className: "text-sm text-gray-600", children: ["Minimum pledge: ", formatCurrency(MIN_PLEDGE_AMOUNT)] }), _jsxs("div", { className: "flex gap-3 pt-2", children: [_jsx(Button, { variant: "outline", size: "lg", onClick: onClose, disabled: isLoading, className: "flex-1", children: "Cancel" }), _jsx(Button, { variant: "primary", size: "lg", onClick: handleContinue, disabled: isLoading || !amount, className: "flex-1", children: "Continue" })] })] })), step === "confirm" && (_jsxs("div", { className: "space-y-4", children: [_jsxs("div", { className: "space-y-3 rounded-lg bg-gray-50 p-4", children: [_jsxs("div", { className: "space-y-2 text-center", children: [_jsx("p", { className: "text-sm text-gray-600", children: "You are pledging" }), _jsx("p", { className: "text-3xl font-bold text-black", children: formatCurrency(parseFloat(amount)) })] }), _jsxs("div", { className: "space-y-2 border-t border-gray-200 pt-3", children: [_jsxs("div", { className: "flex justify-between text-sm", children: [_jsx("span", { className: "text-gray-600", children: "New total pledged" }), _jsx("span", { className: "font-semibold text-black", children: formatCurrency(venture.fundsPledged + parseFloat(amount)) })] }), _jsxs("div", { className: "flex justify-between text-sm", children: [_jsx("span", { className: "text-gray-600", children: "New progress" }), _jsxs("span", { className: "font-semibold text-black", children: [Math.min(100, ((venture.fundsPledged + parseFloat(amount)) / venture.targetGoal) * 100).toFixed(0), "%"] })] })] })] }), _jsx("div", { className: "rounded-lg border border-blue-200 bg-blue-50 p-4", children: _jsx("p", { className: "text-sm text-blue-900", children: "By confirming, you agree to pledge this amount to support this venture. Your pledge will be recorded and the venture creator will be notified." }) }), error && (_jsx("div", { className: "rounded-lg border border-red-200 bg-red-50 p-4", children: _jsx("p", { className: "text-sm text-red-900", children: error }) })), _jsxs("div", { className: "flex gap-3 pt-2", children: [_jsx(Button, { variant: "outline", size: "lg", onClick: handleBack, disabled: isLoading, className: "flex-1", children: "Back" }), _jsx(Button, { variant: "primary", size: "lg", onClick: handleSubmit, disabled: isLoading, className: "flex-1", children: isLoading ? (_jsxs("span", { className: "flex items-center justify-center gap-2", children: [_jsxs("svg", { className: "h-5 w-5 animate-spin", xmlns: "http://www.w3.org/2000/svg", fill: "none", viewBox: "0 0 24 24", children: [_jsx("circle", { className: "opacity-25", cx: "12", cy: "12", r: "10", stroke: "currentColor", strokeWidth: "4" }), _jsx("path", { className: "opacity-75", fill: "currentColor", d: "M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" })] }), "Processing..."] })) : ("Confirm Pledge") })] })] }))] }) }));
};
